/*
 * BB Thumbnail Rail enhancements
 *
 * Augments Horizon's <slideshow-controls thumbnails> vertical rail on PDPs:
 *  1. Auto-scrolls the active thumb into view when the main image changes
 *     (so the rail stays in sync with the gallery the customer is browsing).
 *  2. Injects small up/down chevron buttons that scroll the rail by one
 *     viewport when the rail has more thumbs than fit visible.
 *  3. Buttons disable themselves at the rail edges.
 *
 * Loads on every page; bails early when no vertical-pagination thumbnail
 * rail exists on the page (i.e. anywhere except PDP). No effect on the
 * horizontal mobile thumbnail strip — that's separately styled.
 */

(function () {
  'use strict';

  const RAIL_SELECTOR = 'slideshow-controls[thumbnails][pagination-position="left"], slideshow-controls[thumbnails][pagination-position="right"]';
  const cleanupCallbacks = new WeakMap();

  /**
   * Initialise one rail. Idempotent: marks the rail with data-bb-thumb-rail.
   * @param {HTMLElement} rail
   */
  function initRail(rail) {
    if (rail.dataset.bbThumbRail === 'ready') return;

    const container = rail.querySelector('.slideshow-controls__thumbnails-container');
    const list = rail.querySelector('.slideshow-controls__thumbnails');
    if (!container || !list) return;

    rail.dataset.bbThumbRail = 'ready';

    /* --- Inject prev/next buttons -------------------------------------- */
    const chevronSvg = (dir) => {
      const points = dir === 'up' ? 'M3 8.5L7.5 4 12 8.5' : 'M3 3.5L7.5 8 12 3.5';
      return '<svg viewBox="0 0 15 12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + points + '"/></svg>';
    };

    const makeBtn = (dir, label) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bb-thumb-nav bb-thumb-nav--' + dir;
      b.setAttribute('aria-label', label);
      b.innerHTML = chevronSvg(dir);
      return b;
    };

    const upBtn = makeBtn('up', 'Scroll thumbnails up');
    const downBtn = makeBtn('down', 'Scroll thumbnails down');
    rail.appendChild(upBtn);
    rail.appendChild(downBtn);

    /**
     * Toggle button visibility based on whether scrolling is needed
     * and which edge is currently visible.
     */
    function refreshButtons() {
      const scrollable = container.scrollHeight - container.clientHeight > 1;
      if (!scrollable) {
        upBtn.setAttribute('disabled', '');
        downBtn.setAttribute('disabled', '');
        return;
      }
      const atTop = container.scrollTop <= 1;
      const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
      upBtn.toggleAttribute('disabled', atTop);
      downBtn.toggleAttribute('disabled', atBottom);
    }

    /**
     * Compute how far one "page" of the rail is — used by the buttons.
     */
    function pageStep() {
      // Roughly 3 thumbs at a time so the user gets continuity (one slide
      // remains visible after the scroll). Fallback to clientHeight if the
      // computed thumb size is unreadable.
      const cs = getComputedStyle(rail);
      const thumb = parseFloat(cs.getPropertyValue('--bb-thumb-size')) || 72;
      const gap = parseFloat(cs.getPropertyValue('--bb-thumb-gap')) || 10;
      return (thumb + gap) * 3;
    }

    upBtn.addEventListener('click', () => {
      container.scrollBy({ top: -pageStep(), behavior: 'smooth' });
    });
    downBtn.addEventListener('click', () => {
      container.scrollBy({ top: pageStep(), behavior: 'smooth' });
    });

    container.addEventListener('scroll', refreshButtons, { passive: true });
    refreshButtons();

    /* --- Auto-scroll active thumb into view --------------------------- */
    /**
     * Scroll the currently-active thumb into view if it's not fully visible.
     */
    function scrollActiveIntoView() {
      const active = list.querySelector('.slideshow-control[aria-selected="true"]');
      if (!active) return;

      const cRect = container.getBoundingClientRect();
      const aRect = active.getBoundingClientRect();

      const above = aRect.top < cRect.top + 4;
      const below = aRect.bottom > cRect.bottom - 4;
      if (!above && !below) return;

      const targetTop = active.offsetTop - (container.clientHeight / 2) + (active.offsetHeight / 2);
      container.scrollTo({ top: targetTop, behavior: 'smooth' });
      requestAnimationFrame(refreshButtons);
    }

    // Run once on init in case the page loaded mid-rail.
    requestAnimationFrame(scrollActiveIntoView);

    // Watch for aria-selected changes on any thumb child.
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'aria-selected') {
          if (m.target.getAttribute('aria-selected') === 'true') {
            scrollActiveIntoView();
            return;
          }
        }
      }
    });
    mo.observe(list, { attributes: true, attributeFilter: ['aria-selected'], subtree: true });

    // Resize: viewports change, so visibility cap changes.
    const ro = new ResizeObserver(refreshButtons);
    ro.observe(container);

    cleanupCallbacks.set(rail, () => {
      container.removeEventListener('scroll', refreshButtons);
      mo.disconnect();
      ro.disconnect();
    });
  }

  function initAll(root) {
    (root || document).querySelectorAll(RAIL_SELECTOR).forEach(initRail);
  }

  function cleanupAll(root) {
    (root || document).querySelectorAll(RAIL_SELECTOR).forEach((rail) => {
      const cleanup = cleanupCallbacks.get(rail);
      if (cleanup) {
        cleanup();
        cleanupCallbacks.delete(rail);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement) cleanupAll(event.target);
  });
})();
